import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import {
  User,
  Asset,
  Contract,
  Onboarding,
  Policy,
} from '@core/generated/gql-types';
import {
  getAllRequiredDocumentTags,
  getOrphanAssets,
  getWorkEligibilityDocumentSlots,
  getWorkQualificationDocumentSlots,
  type DocumentSlot,
} from '@core/util/documentSlots.util';
import { useAssetStore } from '@core/asset/useAssetStore';
import { useContractStore } from '@core/contract/useContractStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import { useOnboardingStore } from '@core/onboarding/useOnboardingStore';
import { policyActions } from '@core/policy/policy.action';
import { useAsset } from '@/hooks/useAsset';
import { useTenantId } from '@/contexts/TenantContext';
import { downloadFileFromUrl } from '@/utils/download.util';
import UploadDocumentSlot from './UploadDocumentSlot';
import DocumentSlotRow from './DocumentSlotRow';
import OrphanDocumentRow from './OrphanDocumentRow';
import DocumentPreviewModal from './DocumentPreviewModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import DocumentDetailsModal, {
  type DocumentDetailsModalState,
} from './DocumentDetailsModal';
import WorkerPoliciesSection, {
  getOnboardingAgreeToPolicyFilter,
  type WorkerPoliciesSectionMeta,
} from './WorkerPoliciesSection';

interface DocumentsProps {
  worker: User;
}

const Documents: React.FC<DocumentsProps> = ({ worker }) => {
  const tenantId = useTenantId();

  const [assets, setAssets] = useState<Asset[]>([]);
  const [onboarding, setOnboarding] = useState<Onboarding | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string | null>(null);
  const [assetToDelete, setAssetToDelete] = useState<Asset | null>(null);
  const [deleteDocumentTitle, setDeleteDocumentTitle] = useState<string | null>(
    null,
  );
  const [previewContract, setPreviewContract] = useState<Contract | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [policySectionMeta, setPolicySectionMeta] =
    useState<WorkerPoliciesSectionMeta>({
      filterByOnboarding: false,
      onboardingAgreeToPolicyStepCount: 0,
    });
  const [previewPolicy, setPreviewPolicy] = useState<Policy | null>(null);
  const [detailsModal, setDetailsModal] =
    useState<DocumentDetailsModalState | null>(null);

  const { fetchAssetsForUser, getAssetsForUser } = useAssetStore();
  const { fetchContractsByOwnerId, fetchContractById } = useContractStore();
  const { getTerm } = useTemplateStore();
  const { fetchUserOnboarding } = useOnboardingStore();

  const eligibilitySlots = useMemo(
    () => getWorkEligibilityDocumentSlots(onboarding),
    [onboarding],
  );
  const qualificationSlots = useMemo(
    () => getWorkQualificationDocumentSlots(onboarding),
    [onboarding],
  );
  const orphanAssets = useMemo(() => {
    const requiredTags = getAllRequiredDocumentTags(
      eligibilitySlots,
      qualificationSlots,
    );
    return getOrphanAssets(assets, requiredTags);
  }, [assets, eligibilitySlots, qualificationSlots]);

  const displayContractType = (contractType: Contract['contractType']) => {
    return contractType
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const onComplete = async () => {
    if (worker.id) {
      await fetchAssetsForUser(worker.id);
      setAssets(getAssetsForUser(worker.id));
    }
  };

  const {
    formatUploadedDate,
    getUploadedBy,
    uploadAsset,
    updateAssetMetadata,
    handleDownload,
    handleDelete,
    getFileName,
    getAssetByTag,
  } = useAsset({
    assets,
    userId: worker.id,
    eligibilityTags: eligibilitySlots.map((slot) => slot.tag),
    onComplete,
  });

  const handleOpenUpload = useCallback((slot: DocumentSlot) => {
    setDetailsModal({ slot, asset: null });
  }, []);

  const handleOpenEditDetails = useCallback(
    (slot: DocumentSlot, asset: Asset) => {
      setDetailsModal({ slot, asset });
    },
    [],
  );

  const handleCloseDetailsModal = useCallback(() => {
    setDetailsModal(null);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!worker.id) {
        return;
      }

      try {
        setIsLoading(true);
        const [userAssets, workerContracts, workerOnboarding] =
          await Promise.all([
            fetchAssetsForUser(worker.id).then(() =>
              getAssetsForUser(worker.id),
            ),
            tenantId
              ? fetchContractsByOwnerId(worker.id, tenantId)
              : Promise.resolve([]),
            fetchUserOnboarding(worker.id),
          ]);

        let workerPolicies: Policy[] = [];
        try {
          workerPolicies = await policyActions.getPoliciesByOwnerId(worker.id);
        } catch (policyError) {
          console.error('Error loading worker policies:', policyError);
          workerPolicies = [];
        }

        const agreeFilter = getOnboardingAgreeToPolicyFilter(workerOnboarding);
        setPolicySectionMeta({
          filterByOnboarding: agreeFilter.filter,
          onboardingAgreeToPolicyStepCount: agreeFilter.types.size,
        });
        const filteredPolicies =
          agreeFilter.filter && agreeFilter.types.size > 0
            ? workerPolicies.filter((p) => agreeFilter.types.has(p.policyType))
            : agreeFilter.filter && agreeFilter.types.size === 0
              ? []
              : workerPolicies;

        setAssets(userAssets);
        setOnboarding(workerOnboarding);
        setContracts(workerContracts);
        setPolicies(filteredPolicies);
      } catch (error) {
        console.error('Error loading documents:', error);
        setAssets([]);
        setOnboarding(null);
        setContracts([]);
        setPolicies([]);
      } finally {
        setIsLoading(false);
      }
    };

    void loadData();
  }, [
    worker.id,
    tenantId,
    fetchAssetsForUser,
    getAssetsForUser,
    fetchContractsByOwnerId,
    fetchUserOnboarding,
  ]);

  const formatContractDate = (dateString?: string | null): string => {
    if (!dateString) return '';
    try {
      const date = new Date(parseInt(dateString, 10));
      if (isNaN(date.getTime())) return dateString;
      return format(date, 'd MMM yyyy');
    } catch {
      return dateString;
    }
  };

  const getContractDownloadFilename = useCallback(
    (contract: Contract): string => {
      const firstName = worker.firstName?.replace(/\s+/g, '') || 'Unknown';
      const lastName = worker.lastName?.replace(/\s+/g, '') || '';
      const namePart = [firstName, lastName].filter(Boolean).join('-');
      const datePart = contract.agreedAt
        ? format(
            new Date(
              /^\d+$/.test(contract.agreedAt)
                ? parseInt(contract.agreedAt, 10)
                : contract.agreedAt,
            ),
            'yyyy-MM-dd',
          )
        : contract.id;
      return `contract-${namePart}-${datePart}.pdf`;
    },
    [worker.firstName, worker.lastName],
  );

  const handlePreviewContract = useCallback(
    async (contract: Contract) => {
      try {
        const refreshedContract = await fetchContractById(contract.id);
        setPreviewContract(refreshedContract);
      } catch (error) {
        console.error('Error refreshing worker contract preview URL:', error);
        setPreviewContract(contract);
      } finally {
        setPreviewAsset(null);
        setPreviewTitle(null);
        setPreviewPolicy(null);
      }
    },
    [fetchContractById],
  );

  const handleDownloadContract = useCallback(
    async (contract: Contract) => {
      try {
        const refreshedContract = await fetchContractById(contract.id);
        if (!refreshedContract.pdfUrl) return;

        downloadFileFromUrl(
          refreshedContract.pdfUrl,
          getContractDownloadFilename(contract),
        );
      } catch (error) {
        console.error('Error refreshing worker contract download URL:', error);
        if (!contract.pdfUrl) return;

        downloadFileFromUrl(
          contract.pdfUrl,
          getContractDownloadFilename(contract),
        );
      }
    },
    [fetchContractById, getContractDownloadFilename],
  );

  const handlePreviewDocument = useCallback((asset: Asset, title: string) => {
    setPreviewAsset(asset);
    setPreviewTitle(title);
    setPreviewContract(null);
    setPreviewPolicy(null);
  }, []);

  const handleRejectDocument = useCallback((asset: Asset, title: string) => {
    setAssetToDelete(asset);
    setDeleteDocumentTitle(title);
  }, []);

  const handlePreviewPolicy = useCallback(async (policy: Policy) => {
    try {
      const refreshedPolicy = await policyActions.fetchPolicyById(policy.id);
      setPreviewPolicy(refreshedPolicy);
    } catch (error) {
      console.error('Error refreshing worker policy preview URL:', error);
      setPreviewPolicy(policy);
    } finally {
      setPreviewAsset(null);
      setPreviewTitle(null);
      setPreviewContract(null);
    }
  }, []);

  const renderDocumentSlots = (slots: DocumentSlot[]) => {
    if (slots.length === 0) {
      return (
        <div className="py-4 text-sm text-gray-500">
          No documents required for this worker.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {slots.map((slot) => {
          const asset = getAssetByTag(slot.tag);
          if (!asset) {
            return (
              <UploadDocumentSlot
                key={slot.tag}
                slot={slot}
                onUploadClick={handleOpenUpload}
              />
            );
          }

          const uploadedBy = getUploadedBy(asset);
          const uploadedDate = asset.uploadedDate
            ? formatUploadedDate(asset.uploadedDate)
            : null;

          return (
            <DocumentSlotRow
              key={slot.tag}
              slot={slot}
              asset={asset}
              uploadedBy={uploadedBy}
              uploadedDate={uploadedDate}
              fileName={getFileName(asset)}
              onPreview={handlePreviewDocument}
              onDownload={handleDownload}
              onEditDetails={handleOpenEditDetails}
              onReject={(a) => handleRejectDocument(a, slot.title)}
            />
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <div className="animate-pulse">
          <div className="mb-4 h-6 w-48 rounded bg-gray-200"></div>
          <div className="space-y-4">
            <div className="h-4 w-full rounded bg-gray-200"></div>
            <div className="h-4 w-full rounded bg-gray-200"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          {getTerm(TemplateTerm.WORKER, false)} Contract
        </h3>
        {contracts.length === 0 ? (
          <div className="py-4 text-sm text-gray-500">
            No contract uploaded yet.
          </div>
        ) : (
          <div className="space-y-4">
            {contracts.map((contract) => (
              <div
                key={contract.id}
                className="flex items-center justify-between border-b border-gray-100 pb-4 last:border-b-0"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">
                    {displayContractType(contract.contractType)}
                    {contract.isAgreedTo ? (
                      <span className="ml-2 inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                        Agreed
                      </span>
                    ) : (
                      <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                        Pending
                      </span>
                    )}
                  </div>
                  {contract.isAgreedTo && contract.agreedAt && (
                    <div className="mt-1 text-xs text-gray-500">
                      Agreed on {formatContractDate(contract.agreedAt)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => void handlePreviewContract(contract)}
                    className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <FontAwesomeIcon icon={faEye} className="h-4 w-4" />
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDownloadContract(contract)}
                    className="flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    <FontAwesomeIcon icon={faDownload} className="h-4 w-4" />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-lg bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Work Eligibility Documents
        </h3>
        {renderDocumentSlots(eligibilitySlots)}
      </div>

      <div className="rounded-lg bg-white p-6 shadow-sm">
        <h3 className="mb-4 text-lg font-semibold text-gray-900">
          Work Qualification Documents
        </h3>
        {renderDocumentSlots(qualificationSlots)}
      </div>

      {orphanAssets.length > 0 && (
        <div className="rounded-lg bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-gray-900">
            Other documents
          </h3>
          <p className="mb-4 text-xs text-gray-500">
            Uploaded files that are not part of the worker&apos;s current
            required document list.
          </p>
          <div className="space-y-4">
            {orphanAssets.map((asset) => {
              const title =
                asset.tag?.replace(/([A-Z])/g, ' $1').trim() || 'Document';
              const uploadedDate = asset.uploadedDate
                ? formatUploadedDate(asset.uploadedDate)
                : null;

              return (
                <OrphanDocumentRow
                  key={asset.id}
                  asset={asset}
                  uploadedBy={getUploadedBy(asset)}
                  uploadedDate={uploadedDate}
                  fileName={getFileName(asset)}
                  onPreview={handlePreviewDocument}
                  onDownload={handleDownload}
                  onReject={(a) => handleRejectDocument(a, title)}
                />
              );
            })}
          </div>
        </div>
      )}

      <WorkerPoliciesSection
        worker={worker}
        policies={policies}
        policySectionMeta={policySectionMeta}
        onPreviewPolicy={(policy) => void handlePreviewPolicy(policy)}
      />

      <DocumentDetailsModal
        state={detailsModal}
        onClose={handleCloseDetailsModal}
        onUpload={uploadAsset}
        onUpdateMetadata={updateAssetMetadata}
        currentFileName={
          detailsModal?.asset ? getFileName(detailsModal.asset) : null
        }
      />

      <DeleteConfirmationModal
        assetToDelete={assetToDelete}
        setAssetToDelete={setAssetToDelete}
        documentTitle={deleteDocumentTitle ?? assetToDelete?.tag ?? undefined}
        onDelete={handleDelete}
        onClose={() => setDeleteDocumentTitle(null)}
      />

      <DocumentPreviewModal
        assetPreview={{
          previewAsset,
          setPreviewAsset,
          previewTitle,
          setPreviewTitle,
        }}
        contractPreview={{
          previewContract,
          setPreviewContract,
        }}
        policyPreview={{
          previewPolicy,
          setPreviewPolicy,
        }}
      />
    </div>
  );
};

export default Documents;
